import { useEffect, useState } from 'react';

import { MOCK_EXIT_EVENT } from '../bridge';
import { useStore } from '../store';
import { useT } from '../useT';
import styles from './shell.module.css';

/**
 * Is the developer overlay allowed to draw at all? (UX-101.)
 *
 * The chips were written for a dev build, but the web TV *is* the product on `tv.acapella.lol`
 * and has no native bridge there — so "Bridge simulado", "Foco reparado N×" and the device id
 * sat over a customer's karaoke video. They stay in dev builds, and on any build behind an
 * explicit `?debug=1` (sticky, so it survives the reloads a QA pass makes; `?debug=0` clears
 * it). Nothing is lost by hiding them: every repair still goes to Mixpanel as `focus.repair`.
 */
export const DEBUG_OVERLAY_KEY = 'akp.debugOverlay';

export function devOverlayEnabled(search = ''): boolean {
  const param = new URLSearchParams(search).get('debug');
  const off = param === '0' || param === 'false';
  let sticky = false;
  try {
    if (param !== null) {
      if (off) localStorage.removeItem(DEBUG_OVERLAY_KEY);
      else localStorage.setItem(DEBUG_OVERLAY_KEY, '1');
    }
    sticky = localStorage.getItem(DEBUG_OVERLAY_KEY) === '1';
  } catch {
    sticky = param !== null && !off;
  }
  return import.meta.env.DEV || sticky;
}

/**
 * Dev banners (docs/04 §1): "mock bridge" when `window.AkapellaBridge` is absent and
 * "offline auth" when `/config` has no Firebase project. Both are impossible on a real TV
 * with a configured backend, so they double as a misconfiguration alarm.
 *
 * Plus the focus watchdog's counter (docs/04 §2.1): a chip appears the first time focus had to
 * be repaired, on every build, and names the last fault it fixed. A repair means the D-pad was
 * about to have nothing to aim at, so it is worth seeing even when nothing else looks wrong.
 */
export function DevOverlay() {
  const { t } = useT();
  const bridge = useStore((s) => s.device.bridge);
  const deviceId = useStore((s) => s.device.id);
  const authMode = useStore((s) => s.authMode);
  const bootStatus = useStore((s) => s.boot.status);
  const focusRepairs = useStore((s) => s.ui.focusRepairCount);
  const lastRepair = useStore((s) => s.ui.focusRepairs[s.ui.focusRepairs.length - 1] ?? null);
  const [exited, setExited] = useState(false);
  const [enabled] = useState(() =>
    devOverlayEnabled(typeof window === 'undefined' ? '' : window.location.search),
  );

  useEffect(() => {
    const onExit = () => setExited(true);
    window.addEventListener(MOCK_EXIT_EVENT, onExit);
    return () => window.removeEventListener(MOCK_EXIT_EVENT, onExit);
  }, []);

  if (!enabled || bootStatus === 'idle') return null;
  const mock = bridge === 'mock';
  const offline = authMode === 'offline' && bootStatus === 'ready';
  if (!mock && !offline && !exited && focusRepairs === 0) return null;
  return (
    <div className={styles.devOverlay} data-testid="dev-overlay">
      {exited ? <span className={styles.devChip}>exitApp()</span> : null}
      {mock ? (
        <span className={styles.devChip} data-testid="dev-mock-bridge">
          {t('tv:dev.mockBridge')}
        </span>
      ) : null}
      {offline ? (
        <span className={styles.devChip} data-testid="dev-offline-auth">
          {t('tv:dev.offlineAuth')}
        </span>
      ) : null}
      {focusRepairs > 0 && lastRepair ? (
        <span className={styles.devChip} data-testid="dev-focus-repair">
          {t('tv:dev.focusRepair', {
            repairs: focusRepairs,
            fault: lastRepair.fault,
            screen: lastRepair.screen,
          })}
        </span>
      ) : null}
      {mock && deviceId ? (
        <span className={`${styles.devChip} ${styles.devChipMuted}`}>
          {t('tv:dev.deviceId', { id: deviceId.slice(0, 8) })}
        </span>
      ) : null}
    </div>
  );
}
